import { useMemo, useState } from 'react';
import { useLocation, useOutlet } from 'react-router-dom';
import { Expand } from 'lucide-react';
import { Breadcrumb } from '@/versions/v1/chrome/Breadcrumb';
import { crumbFor } from '@/versions/v1/shared/oem-nav';
import { ExpandView } from '@/versions/v1/shared/ExpandView';
import { useFullWidth } from '@/versions/v1/shared/useFullWidth';
import { OemTopBar } from './OemTopBar';
// Registering the palette's records source is a side effect of this import.
import './records-palette';

// =========================================================================
// OemShell — the scene column: top bar, breadcrumb, route outlet.
// Fork-owned (zone C). Passed as `main` from app/layout.tsx, which is a
// one-line seam.
//
// The breadcrumb is the foundation's `chrome/Breadcrumb.tsx` unchanged, fed
// by `crumbFor()` — the OEM nav owns the Home › Section › View derivation, so
// scenes never assemble their own crumbs.
//
// Scroll lives on the outlet wrapper, not on the page: the top bar and crumb
// stay put while a long fleet table scrolls, matching the demo's sticky
// `.crumb`.
//
// The shell also owns the two presenter controls: full width (the top-bar
// button and the F key, `useFullWidth`) and the "Expand view" overlay, whose
// trigger sits at the right of the crumb row as in the demo. The overlay
// renders the route's element a second time, so it is always the page the
// crumb names.
// =========================================================================

export function OemShell() {
  // fork(qa2.Q2): the assets sub-class pages live in the query string, so the
  // crumb needs `search` as well as the path. Every other route ignores it.
  const { pathname, search } = useLocation();
  const outlet = useOutlet();
  const { fullWidth, toggleFullWidth } = useFullWidth();
  const [expanded, setExpanded] = useState(false);

  const crumbs = useMemo(() => crumbFor(pathname, search), [pathname, search]);
  const pageName = crumbs.length ? crumbs[crumbs.length - 1].label : 'Page';

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      <OemTopBar fullWidth={fullWidth} onToggleFullWidth={toggleFullWidth} />

      <div
        className="flex flex-shrink-0 items-center gap-3 px-6 pb-2 pt-3"
        style={{ borderBottom: '1px solid var(--hairline)' }}
      >
        <div className="min-w-0 flex-1">
          <Breadcrumb segments={crumbs} />
        </div>
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-md px-2 py-0.5 text-xs transition-colors hover:bg-zinc-900/[0.04] dark:hover:bg-white/[0.04]"
          style={{ color: 'var(--ink-tertiary)' }}
          title="Open this page full-screen, with zoom and pan"
          aria-haspopup="dialog"
        >
          <Expand className="h-3.5 w-3.5" aria-hidden="true" />
          Expand view
        </button>
      </div>

      <div className="scrollbar-subtle min-h-0 flex-1 overflow-y-auto">{outlet}</div>

      <ExpandView open={expanded} title={pageName} onClose={() => setExpanded(false)}>
        {outlet}
      </ExpandView>
    </div>
  );
}
